import { useCallback, useEffect, useRef, useState } from 'react';

export const USER_ACTIVITY_EVENT = 'vv-user-activity';

const DEFAULT_IDLE_MS = 15 * 60 * 1000;
const DEFAULT_WARN_MS = 60 * 1000;

interface UseIdleTimeoutOptions {
  enabled: boolean;
  idleMs?: number;
  warnMs?: number;
  onTimeout: () => void;
}

interface UseIdleTimeoutResult {
  warning: boolean;
  secondsRemaining: number;
  stay: () => void;
}

/** Dispatch from Unity event handlers or other activity sources. */
export function signalUserActivity(): void {
  window.dispatchEvent(new CustomEvent(USER_ACTIVITY_EVENT));
}

/**
 * Frontend-driven in-world idle timeout. Warns the user before disconnecting.
 */
export function useIdleTimeout({
  enabled,
  idleMs = DEFAULT_IDLE_MS,
  warnMs = DEFAULT_WARN_MS,
  onTimeout,
}: UseIdleTimeoutOptions): UseIdleTimeoutResult {
  const [warning, setWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const onTimeoutRef = useRef(onTimeout);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setWarning(false);
    setSecondsRemaining(0);
  }, []);

  useEffect(() => {
    if (!enabled) {
      setWarning(false);
      setSecondsRemaining(0);
      return;
    }

    const domEvents = ['mousemove', 'keydown', 'pointerdown', 'wheel', 'touchstart'] as const;
    const onDomActivity = () => resetActivity();
    const onCustomActivity = () => resetActivity();

    domEvents.forEach((evt) => window.addEventListener(evt, onDomActivity, { passive: true }));
    window.addEventListener(USER_ACTIVITY_EVENT, onCustomActivity);

    const tick = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const warnAt = idleMs - warnMs;

      if (elapsed >= idleMs) {
        setWarning(false);
        setSecondsRemaining(0);
        onTimeoutRef.current();
        return;
      }

      if (elapsed >= warnAt) {
        const remaining = Math.max(0, Math.ceil((idleMs - elapsed) / 1000));
        setWarning(true);
        setSecondsRemaining(remaining);
      } else {
        setWarning(false);
        setSecondsRemaining(0);
      }
    }, 1000);

    return () => {
      domEvents.forEach((evt) => window.removeEventListener(evt, onDomActivity));
      window.removeEventListener(USER_ACTIVITY_EVENT, onCustomActivity);
      clearInterval(tick);
    };
  }, [enabled, idleMs, resetActivity, warnMs]);

  return { warning, secondsRemaining, stay: resetActivity };
}
